import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchFilters from '../../components/search/SearchFilters';
import SearchTable from '../../components/search/SearchTable';
import { searchService } from '../../services/api';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const Search = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const query = searchParams.get('q') || '';
  const type = searchParams.get('type') || 'players';
  const page = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query) {
        setSearchResults([]);
        setPagination(null);
        return;
      }
      
      setLoading(true);
      // Reset results when type changes to prevent stale data
      setSearchResults([]);
      try {
        const params = {
          type,
          query,
          page,
          per_page: 25,
        };

        // Platform user filter - new API uses platform_only
        if (['players', 'clubs'].includes(type) && filters.is_platform_user) {
          params.platform_only = true;
        }

        console.log(filters)
        
        // Type-specific filters based on new Wyscout API
        if (type === 'players') {
          // Level filter (pro/youth) - defaults to pro
          if (filters.level) {
            params.level = filters.level; // pro/youth
          } else {
            params.level = 'pro'; // default
          }
          
          // Nationality/Country filter - accepts alpha2 or alpha3 country code
          if (filters.country) {
            params.country = filters.country; // ISO alpha2/alpha3
          }
          
          // Position filter - supports position codes (GK, GKP, DF, DEF, MD, MID, FW, FWD)
          if (filters.position) {
            params.position = filters.position; // expects position code or ID
          }
          
          // Division Country filter - used to fetch leagues
          if (filters.divisionCountry) {
            // This is used internally to fetch leagues, not sent to search API
          }
          
          // Competition/Division filter
          if (filters.competition_id) {
            params.competition_id = filters.competition_id; // expects competition ID
          }
        }
        
        if (type === 'clubs') {
          // Country filter - accepts alpha2 or alpha3 country code
          if (filters.country) {
            params.country = filters.country;
          }
        }

        // Other types (coaches, etc.) - no filters

        const response = await searchService.search(params);
        
        if (response.success) {
          console.log("search result", response.data, type)
          // New API structure - data is nested under results
          let resultsData = response.data?.results?.[type] || [];
          
          // Ensure resultsData is always an array
          if (!Array.isArray(resultsData)) {
            resultsData = [];
          }

          console.log("resultsData", resultsData)  
          setSearchResults(resultsData);

          // Normalize pagination shape and compute has_more fallback
          const p = response.pagination || {};
          const currentPage = p.current_page || p.page || page || 1;
          const perPage = p.per_page || 25;
          const hasMore = typeof p.has_more === 'boolean'
            ? p.has_more
            : (typeof p.next !== 'undefined'
                ? !!p.next
                : (Array.isArray(resultsData) && resultsData.length >= perPage));

          setPagination({ ...p, current_page: currentPage, per_page: perPage, has_more: hasMore });
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query, type, page, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePageChange = (newPage) => {
    navigate({
      pathname: '/search',
      search: `?q=${query}&type=${type}&page=${newPage}`
    });
  };  

  const handleClearAllFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== null && value !== undefined);

  return (
    <div className="mx-auto py-4">
      {/* Desktop header */}
      {!isMobile && (
        <div className="flex items-center justify-between px-12 pb-4 border-b border-[#E5E6E8]">
          <h1 className="text-[16px] font-medium text-[#202630]">
            Search results for '{query}'
          </h1>
          <div className="flex items-center gap-2">
            {['players', 'clubs'].includes(type) && (
              <>
                <span className="text-[16px] font-medium text-[#1A212F]">Registered with Transferpitch</span>
                <button 
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    filters.is_platform_user ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  onClick={() => handleFilterChange('is_platform_user', !filters.is_platform_user)}
                  disabled={loading}
                >
                  <span
                    className={`${
                      filters.is_platform_user ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out shadow-lg`}
                  />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile header - in separate rows as requested */}
      {isMobile && (
        <>
          {/* Row 1: Search results for 'query' */}
          <div className="px-4 py-2 border-b border-[#E5E6E8]">
            <h1 className="text-[14px] font-normal text-[#202630]">
              Search results for '{query}'
            </h1>
          </div>
          
          {/* Row 2: Registered with Transferpitch toggle */}
          {['players', 'clubs'].includes(type) && (
            <div className="px-4 py-2 border-b border-[#E5E6E8] flex items-center justify-between">
              <span className="text-[14px] font-normal text-[#202630]">Registered with Transferpitch</span>
              <button 
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  filters.is_platform_user ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                onClick={() => handleFilterChange('is_platform_user', !filters.is_platform_user)}
                disabled={loading}
              >
                <span
                  className={`${
                    filters.is_platform_user ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out shadow-lg`}
                />
              </button>
            </div>
          )}
        </>
      )}

      <div className={`mb-6 relative ${isMobile ? 'px-4' : 'px-12'} py-4 border-b border-[#E5E6E8]`}>
        {/* Desktop: Clear All button next to filters */}
        {!isMobile && hasActiveFilters && (
          <div className="flex items-center justify-end mb-4">
            <button
              onClick={handleClearAllFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              disabled={loading}
            >
              Clear All
            </button>
          </div>
        )}
        
        <SearchFilters
          type={type}
          filters={filters}
          onFilterChange={handleFilterChange}
          disabled={loading}
        />
        
        {/* Mobile: Clear All button below filters */}
        {isMobile && hasActiveFilters && (
          <div className="flex justify-center mt-4">
            <button
              onClick={handleClearAllFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium px-4 py-2 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              disabled={loading}
            >
              Clear All Filters
            </button>
          </div>
        )}
        
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      <SearchTable
        type={type}
        data={searchResults}
        loading={loading}
      />

      {/* Pagination - only show when not loading and has results */}
      {!loading && pagination && searchResults.length > 0 && (
        <div className="mt-6 flex justify-center">
          <nav className="flex items-center gap-2">
            {pagination.current_page && pagination.current_page > 1 && (
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                className="px-3 py-1 border rounded hover:bg-gray-50"
                disabled={loading}
              >
                <FaChevronLeft />
              </button>
            )}
            <span className="px-3 py-1">
              Page {pagination.current_page}
            </span>
            {pagination.has_more && (
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                className="px-3 py-1 border rounded hover:bg-gray-50"
                disabled={loading}
              >
                <FaChevronRight />
              </button>
            )}
          </nav>
        </div>
      )}
    </div>
  );
};

export default Search; 